export type AppDialogType = 'info' | 'success' | 'error' | 'warning';

export interface AppDialogAlertOptions {
  title?: string;
  type?: AppDialogType;
  confirmText?: string;
}

export interface AppDialogConfirmOptions extends AppDialogAlertOptions {
  cancelText?: string;
  destructive?: boolean;
}

export interface AppDialogPromptOptions extends AppDialogAlertOptions {
  cancelText?: string;
  defaultValue?: string;
  placeholder?: string;
  inputLabel?: string;
}

type AppDialogBridge = {
  alert: (message: string, options?: AppDialogAlertOptions) => Promise<void>;
  confirm: (message: string, options?: AppDialogConfirmOptions) => Promise<boolean>;
  prompt: (message: string, options?: AppDialogPromptOptions) => Promise<string | null>;
};

let bridge: AppDialogBridge | null = null;

export function registerAppDialog(nextBridge: AppDialogBridge | null) {
  bridge = nextBridge;
}

export function inferDialogType(message: string): AppDialogType {
  const trimmed = message.trim();
  if (trimmed.startsWith('✅') || /exitosamente|successfully/i.test(trimmed)) {
    return 'success';
  }
  if (trimmed.startsWith('❌') || trimmed.startsWith('Error') || trimmed.startsWith('⚠️')) {
    return trimmed.startsWith('⚠️') ? 'warning' : 'error';
  }
  if (/^error/i.test(trimmed) || /falló|failed/i.test(trimmed)) {
    return 'error';
  }
  return 'info';
}

export const appDialog = {
  alert(message: string, options?: AppDialogAlertOptions): Promise<void> {
    if (!bridge) {
      console.warn('[appDialog] Provider no montado:', message);
      return Promise.resolve();
    }
    return bridge.alert(message, {
      type: options?.type ?? inferDialogType(message),
      ...options,
    });
  },

  confirm(message: string, options?: AppDialogConfirmOptions): Promise<boolean> {
    if (!bridge) {
      console.warn('[appDialog] Provider no montado (confirm):', message);
      return Promise.resolve(false);
    }
    return bridge.confirm(message, {
      type: options?.type ?? inferDialogType(message),
      ...options,
    });
  },

  prompt(message: string, options?: AppDialogPromptOptions): Promise<string | null> {
    if (!bridge) {
      console.warn('[appDialog] Provider no montado (prompt):', message);
      return Promise.resolve(null);
    }
    return bridge.prompt(message, options);
  },
};
